'use client'

import { RegistrationAction } from '@gql-generated/graphql'
import { atom, useAtom, useAtomValue } from 'jotai'
import { atomWithReset, useResetAtom } from 'jotai/utils'
import { useCallback, useMemo } from 'react'

export const AccountCreationSteps = {
  COMPANY_NATIONAL_ID: 'COMPANY_NATIONAL_ID',
  [RegistrationAction.Doa]: RegistrationAction.Doa,
  [RegistrationAction.ExpressDoc]: RegistrationAction.ExpressDoc,
  [RegistrationAction.ClassicDoc]: RegistrationAction.ClassicDoc,
} as const

type StepKey = keyof typeof AccountCreationSteps

export const authPathAtom = atomWithReset<StepKey>('COMPANY_NATIONAL_ID')
export const currentStepIndexAtom = atomWithReset(0)

type AuthFormStep =
  | 'COMPANY_NATIONAL_ID'
  | 'ACTIVITY'
  | 'STORE'
  | 'USER_INFO'
  | 'DOA'
  | 'FORM_VALIDATION'
  | 'TERMS'
  | 'SUBMIT'

const AUTH_STEPS_MAP: Record<StepKey, (AuthFormStep | '')[]> = {
  COMPANY_NATIONAL_ID: ['COMPANY_NATIONAL_ID', '', '', ''],
  [RegistrationAction.Doa]: ['COMPANY_NATIONAL_ID', 'DOA', 'FORM_VALIDATION', 'TERMS', 'SUBMIT'],
  [RegistrationAction.ExpressDoc]: [
    'COMPANY_NATIONAL_ID',
    'ACTIVITY',
    'STORE',
    'USER_INFO',
    'TERMS',
    'SUBMIT',
  ],
  [RegistrationAction.ClassicDoc]: [
    'COMPANY_NATIONAL_ID',
    'ACTIVITY',
    'STORE',
    'USER_INFO',
    'TERMS',
    'SUBMIT',
  ],
}

const currentStepAtom = atom((get) => {
  const path = get(authPathAtom)
  const index = get(currentStepIndexAtom)

  return AUTH_STEPS_MAP[path][index] ?? ''
})

export function useAuthProgress() {
  const authPath = useAtomValue(authPathAtom)
  const resetAuthPath = useResetAtom(authPathAtom)
  const [currentIndex, setStepIndex] = useAtom(currentStepIndexAtom)
  const currentStep = useAtomValue(currentStepAtom)

  const { progressPercentage, nextIndex, previousIndex, totalSteps } = useMemo(() => {
    const steps = AUTH_STEPS_MAP[authPath]
    const total = steps.length
    const nextIndex = currentIndex + 1
    const previousIndex = currentIndex - 1
    const percent = Math.round(((currentIndex + 1) / total) * 100)

    return {
      progressPercentage: percent,
      nextIndex,
      previousIndex,
      totalSteps: total,
    }
  }, [authPath, currentIndex])

  const nextStep = useCallback(() => {
    if (nextIndex < totalSteps) {
      setStepIndex(nextIndex)
    }
  }, [nextIndex, setStepIndex, totalSteps])

  const previousStep = useCallback(() => {
    if (previousIndex > 0) {
      setStepIndex(previousIndex)
    }
  }, [previousIndex, setStepIndex])

  const goToStepByIndex = useCallback(
    (step: number) => {
      if (step >= 0 && step < totalSteps) {
        setStepIndex(step)
      }
    },
    [setStepIndex, totalSteps],
  )

  const goToStep = useCallback(
    (step: AuthFormStep) => {
      const stepIndex = AUTH_STEPS_MAP[authPath].indexOf(step)
      goToStepByIndex(stepIndex)
    },
    [authPath, goToStepByIndex],
  )

  const resetStep = useCallback(() => {
    resetAuthPath()
    setStepIndex(0)
  }, [resetAuthPath, setStepIndex])

  return {
    progressPercentage,
    currentStep,
    currentIndex,
    nextStep,
    previousStep,
    goToStep,
    goToStepByIndex,
    resetStep,
    totalSteps,
  }
}
